import React from 'react';
import { useTranslation } from 'react-i18next';

import PolicyNavigation from './PolicyNavigation';

interface TrustCenterContentProps {
  onNavigate?: (view: string) => void;
}

const TrustCenterContent: React.FC<TrustCenterContentProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-4xl mx-auto vscode-text-primary pt-8">
      <h1 className="text-3xl font-bold mb-6 vscode-text-primary">{t('legal.trustTitle')}</h1>
      
      <div className="prose max-w-none">
        <p className="text-sm vscode-text-secondary mb-6">
          <strong>{t('legal.trust.lastUpdated')}:</strong> {t('legal.trust.lastUpdatedValue')}<br />
          <strong>{t('legal.trust.version')}:</strong> {t('legal.trust.versionValue')}
        </p>

        <p className="text-lg vscode-text-secondary mb-6">
          {t('legal.trust.intro')}
        </p>

        <div className="mb-8 p-4  ">
          <p className="vscode-text-secondary">
            {t('legal.trust.commitment')}
          </p>
        </div>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s1Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s1_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s1_1Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s1_1_1')}</li>
          <li>{t('legal.trust.s1_1_2')}</li>
          <li>{t('legal.trust.s1_1_3')}</li>
          <li>{t('legal.trust.s1_1_4')}</li>
          <li>{t('legal.trust.s1_1_5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s1_2Title')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className=" p-4-lg">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.p1Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.p1Desc')}</p>
          </div>
          <div className=" p-4-lg">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.p2Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.p2Desc')}</p>
          </div>
          <div className=" p-4-lg">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.p3Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.p3Desc')}</p>
          </div>
          <div className=" p-4-lg">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.p4Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.p4Desc')}</p>
          </div>
        </div>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s2Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s2_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s2_1Intro')}
        </p>
        <div className=" p-6-lg mb-4">
          <table className="w-full text-sm vscode-text-secondary">
            <thead>
              <tr className="border-b vscode-border">
                <th className="text-left py-2 vscode-text-primary">{t('legal.trust.thService')}</th>
                <th className="text-left py-2 vscode-text-primary">{t('legal.trust.thProvider')}</th>
                <th className="text-left py-2 vscode-text-primary">{t('legal.trust.thCert')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.trust.r1Service')}</td>
                <td className="py-2">{t('legal.trust.r1Provider')}</td>
                <td className="py-2">{t('legal.trust.r1Cert')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.trust.r2Service')}</td>
                <td className="py-2">{t('legal.trust.r2Provider')}</td>
                <td className="py-2">{t('legal.trust.r2Cert')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.trust.r3Service')}</td>
                <td className="py-2">{t('legal.trust.r3Provider')}</td>
                <td className="py-2">{t('legal.trust.r3Cert')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.trust.r4Service')}</td>
                <td className="py-2">{t('legal.trust.r4Provider')}</td>
                <td className="py-2">{t('legal.trust.r4Cert')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s2_2Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s2_2_1')}</li>
          <li>{t('legal.trust.s2_2_2')}</li>
          <li>{t('legal.trust.s2_2_3')}</li>
          <li>{t('legal.trust.s2_2_4')}</li>
          <li>{t('legal.trust.s2_2_5')}</li>
          <li>{t('legal.trust.s2_2_6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s2_3Title')}</h3>
        <div className=" p-4-lg mb-4">
          <ul className="vscode-text-secondary space-y-2">
            <li>{t('legal.trust.s2_3_1')}</li>
            <li>{t('legal.trust.s2_3_2')}</li>
            <li>{t('legal.trust.s2_3_3')}</li>
            <li>{t('legal.trust.s2_3_4')}</li>
            <li>{t('legal.trust.s2_3_5')}</li>
          </ul>
        </div>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s3Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s3_1Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s3_1_1')}</li>
          <li>{t('legal.trust.s3_1_2')}</li>
          <li>{t('legal.trust.s3_1_3')}</li>
          <li>{t('legal.trust.s3_1_4')}</li>
          <li>{t('legal.trust.s3_1_5')}</li>
          <li>{t('legal.trust.s3_1_6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s3_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s3_2Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s3_2_1')}</li>
          <li>{t('legal.trust.s3_2_2')}</li>
          <li>{t('legal.trust.s3_2_3')}</li>
          <li>{t('legal.trust.s3_2_4')}</li>
          <li>{t('legal.trust.s3_2_5')}</li>
          <li>{t('legal.trust.s3_2_6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s3_3Title')}</h3>
        <div className=" p-4-lg mb-4">
          <ul className="vscode-text-secondary space-y-2">
            <li>{t('legal.trust.s3_3_1')}</li>
            <li>{t('legal.trust.s3_3_2')}</li>
            <li>{t('legal.trust.s3_3_3')}</li>
            <li>{t('legal.trust.s3_3_4')}</li>
            <li>{t('legal.trust.s3_3_5')}</li>
          </ul>
        </div>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s4Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s4_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s4_1Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s4_1_1')}</li>
          <li>{t('legal.trust.s4_1_2')}</li>
          <li>{t('legal.trust.s4_1_3')}</li>
          <li>{t('legal.trust.s4_1_4')}</li>
          <li>{t('legal.trust.s4_1_5')}</li>
          <li>{t('legal.trust.s4_1_6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s4_2Title')}</h3>
        <div className=" p-4-lg mb-4">
          <p className="vscode-text-secondary mb-3"><strong>{t('legal.trust.s4_2Intro')}</strong></p>
          <ul className="vscode-text-secondary space-y-2 ml-4">
            <li>• {t('legal.trust.s4_2_1')}</li>
            <li>• {t('legal.trust.s4_2_2')}</li>
            <li>• {t('legal.trust.s4_2_3')}</li>
            <li>• {t('legal.trust.s4_2_4')}</li>
            <li>• {t('legal.trust.s4_2_5')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s4_3Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s4_3_1')}</li>
          <li>{t('legal.trust.s4_3_2')}</li>
          <li>{t('legal.trust.s4_3_3')}</li>
          <li>{t('legal.trust.s4_3_4')}</li>
          <li>{t('legal.trust.s4_3_5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s4_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s4_4Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s4_4_1')}</li>
          <li>{t('legal.trust.s4_4_2')}</li>
          <li>{t('legal.trust.s4_4_3')}</li>
          <li>{t('legal.trust.s4_4_4')}</li>
          <li>{t('legal.trust.s4_4_5')}</li>
        </ul>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s5Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s5_1Title')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className=" p-4 ">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.c1Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.c1Desc')}</p>
          </div>
          <div className=" p-4 ">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.c2Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.c2Desc')}</p>
          </div>
          <div className=" p-4 ">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.c3Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.c3Desc')}</p>
          </div>
          <div className=" p-4 ">
            <h4 className="text-lg font-semibold mb-2 vscode-text-primary">{t('legal.trust.c4Title')}</h4>
            <p className="text-sm vscode-text-secondary">{t('legal.trust.c4Desc')}</p>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s5_2Title')}</h3>
        <div className=" p-4  mb-4">
          <ul className="vscode-text-secondary space-y-2">
            <li>{t('legal.trust.s5_2_1')}</li>
            <li>{t('legal.trust.s5_2_2')}</li>
            <li>{t('legal.trust.s5_2_3')}</li>
            <li>{t('legal.trust.s5_2_4')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s5_3Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s5_3_1')}</li>
          <li>{t('legal.trust.s5_3_2')}</li>
          <li>{t('legal.trust.s5_3_3')}</li>
          <li>{t('legal.trust.s5_3_4')}</li>
        </ul>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s6Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s6_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s6_1Intro')}
        </p>
        <div className=" p-6-lg mb-4">
          <ol className="vscode-text-secondary space-y-3 ml-4">
            <li>{t('legal.trust.s6_1_1')}</li>
            <li>{t('legal.trust.s6_1_2')}</li>
            <li>{t('legal.trust.s6_1_3')}</li>
            <li>{t('legal.trust.s6_1_4')}</li>
            <li>{t('legal.trust.s6_1_5')}</li>
          </ol>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s6_2Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s6_2_1')}</li>
          <li>{t('legal.trust.s6_2_2')}</li>
          <li>{t('legal.trust.s6_2_3')}</li>
          <li>{t('legal.trust.s6_2_4')}</li>
          <li>{t('legal.trust.s6_2_5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s6_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s6_3Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s6_3_1')}</li>
          <li>{t('legal.trust.s6_3_2')}</li>
          <li>{t('legal.trust.s6_3_3')}</li>
          <li>{t('legal.trust.s6_3_4')}</li>
          <li>{t('legal.trust.s6_3_5')}</li>
        </ul>

        {/* Section 7 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s7Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s7_1Title')}</h3>
        <div className=" p-4-lg mb-4">
          <table className="w-full text-sm vscode-text-secondary">
            <thead>
              <tr className="border-b vscode-border">
                <th className="text-left py-2 vscode-text-primary">{t('legal.trust.thData')}</th>
                <th className="text-left py-2 vscode-text-primary">{t('legal.trust.thFreq')}</th>
                <th className="text-left py-2 vscode-text-primary">{t('legal.trust.thRet')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.trust.b1Data')}</td>
                <td className="py-2">{t('legal.trust.b1Freq')}</td>
                <td className="py-2">{t('legal.trust.b1Ret')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.trust.b2Data')}</td>
                <td className="py-2">{t('legal.trust.b2Freq')}</td>
                <td className="py-2">{t('legal.trust.b2Ret')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.trust.b3Data')}</td>
                <td className="py-2">{t('legal.trust.b3Freq')}</td>
                <td className="py-2">{t('legal.trust.b3Ret')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.trust.b4Data')}</td>
                <td className="py-2">{t('legal.trust.b4Freq')}</td>
                <td className="py-2">{t('legal.trust.b4Ret')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s7_2Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s7_2_1')}</li>
          <li>{t('legal.trust.s7_2_2')}</li>
          <li>{t('legal.trust.s7_2_3')}</li>
          <li>{t('legal.trust.s7_2_4')}</li>
          <li>{t('legal.trust.s7_2_5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s7_3Title')}</h3>
        <div className=" p-4-lg mb-4">
          <ul className="vscode-text-secondary space-y-2">
            <li>{t('legal.trust.s7_3_1')}</li>
            <li>{t('legal.trust.s7_3_2')}</li>
            <li>{t('legal.trust.s7_3_3')}</li>
          </ul>
        </div>

        {/* Section 8 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s8Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s8_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s8_1Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s8_1_1')}</li>
          <li>{t('legal.trust.s8_1_2')}</li>
          <li>{t('legal.trust.s8_1_3')}</li>
          <li>{t('legal.trust.s8_1_4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s8_2Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s8_2_1')}</li>
          <li>{t('legal.trust.s8_2_2')}</li>
          <li>{t('legal.trust.s8_2_3')}</li>
          <li>{t('legal.trust.s8_2_4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s8_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s8_3Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s8_3_1')}</li>
          <li>{t('legal.trust.s8_3_2')}</li>
          <li>{t('legal.trust.s8_3_3')}</li>
          <li>{t('legal.trust.s8_3_4')}</li>
          <li>{t('legal.trust.s8_3_5')}</li>
        </ul>

        {/* Section 9 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s9Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s9_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s9_1Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s9_1_1')}</li>
          <li>{t('legal.trust.s9_1_2')}</li>
          <li>{t('legal.trust.s9_1_3')}</li>
          <li>{t('legal.trust.s9_1_4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s9_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s9_2Intro')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s9_2_1')}</li>
          <li>{t('legal.trust.s9_2_2')}</li>
          <li>{t('legal.trust.s9_2_3')}</li>
          <li>{t('legal.trust.s9_2_4')}</li>
        </ul>

        {/* Section 10 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4 vscode-text-primary">{t('legal.trust.s10Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s10_1Title')}</h3>
        <div className=" p-6-lg mb-4">
          <p className="vscode-text-secondary mb-2">
            <strong>{t('legal.trust.s10_1_1')}</strong> <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary mb-2">
            <strong>{t('legal.trust.s10_1_2')}</strong> <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary mb-2">
            <strong>{t('legal.trust.s10_1_3')}</strong> <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary">
            <strong>{t('legal.trust.s10_1_4')}</strong> <a href="mailto:support@gograbjob.com" className="text-[var(--vscode-text-link)] hover:opacity-90">support@gograbjob.com</a>
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 vscode-text-primary">{t('legal.trust.s10_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s10_2Intro')}
        </p>
        <ol className="list-decimal list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s10_2_1')}</li>
          <li>{t('legal.trust.s10_2_2')}</li>
          <li>{t('legal.trust.s10_2_3')}</li>
          <li>{t('legal.trust.s10_2_4')}</li>
        </ol>
        <p className="vscode-text-secondary mb-4">
          {t('legal.trust.s10_2Commit')}
        </p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.trust.s10_2_5')}</li>
          <li>{t('legal.trust.s10_2_6')}</li>
          <li>{t('legal.trust.s10_2_7')}</li>
          <li>{t('legal.trust.s10_2_8')}</li>
        </ul>

        {/* Summary */}
        <div className="mt-8 p-6  ">
          <h3 className="text-xl font-semibold mb-3 vscode-text-primary">{t('legal.trust.summaryTitle')}</h3>
          <div className="space-y-2 vscode-text-secondary">
            <p>{t('legal.trust.summary_1')}</p>
            <p>{t('legal.trust.summary_2')}</p>
            <p>{t('legal.trust.summary_3')}</p>
            <p>{t('legal.trust.summary_4')}</p>
            <p>{t('legal.trust.summary_5')}</p>
            <p>{t('legal.trust.summary_6')}</p>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 p-4 vscode-bg-elevated rounded-lg border vscode-border">
          <p className="text-sm vscode-text-secondary mb-2">
            <strong>{t('legal.trust.relatedTitle')}</strong>
          </p>
          <ul className="text-sm vscode-text-secondary space-y-1">
            <li>• <a href="/privacy-policy" className="text-[var(--vscode-text-link)] hover:opacity-90">{t('legal.trust.privacyLink')}</a></li>
            <li>• <a href="/gdpr-compliance" className="text-[var(--vscode-text-link)] hover:opacity-90">{t('legal.trust.gdprLink')}</a></li>
            <li>• <a href="/terms-of-service" className="text-[var(--vscode-text-link)] hover:opacity-90">{t('legal.trust.termsLink')}</a></li>
            <li>• <a href="/cookie-policy" className="text-[var(--vscode-text-link)] hover:opacity-90">{t('legal.trust.cookieLink')}</a></li>
          </ul>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 p-6 -lg text-center">
          <p className="text-sm vscode-text-secondary mb-4">{t('legal.trust.badgeIntro')}</p>
          <div className="flex flex-wrap justify-center gap-6 vscode-text-secondary">
            <div className="text-center px-4 py-2 vscode-bg-elevated rounded-lg border vscode-border">
              <p className="text-sm font-semibold vscode-text-primary">{t('legal.trust.badgeSsl')}</p>
            </div>
            <div className="text-center px-4 py-2 vscode-bg-elevated rounded-lg border vscode-border">
              <p className="text-sm font-semibold vscode-text-primary">{t('legal.trust.badgeGdpr')}</p>
            </div>
            <div className="text-center px-4 py-2 vscode-bg-elevated rounded-lg border vscode-border">
              <p className="text-sm font-semibold vscode-text-primary">{t('legal.trust.badgeCcpa')}</p>
            </div>
            <div className="text-center px-4 py-2 vscode-bg-elevated rounded-lg border vscode-border">
              <p className="text-sm font-semibold vscode-text-primary">{t('legal.trust.badgeAes')}</p>
            </div>
            <div className="text-center px-4 py-2 vscode-bg-elevated rounded-lg border vscode-border">
              <p className="text-sm font-semibold vscode-text-primary">{t('legal.trust.badgeSoc2')}</p>
            </div>
          </div>
          <p className="text-xs vscode-text-tertiary mt-4">{t('legal.trust.inProgress')}</p>
        </div>
      </div>

      {/* Related Policies Navigation */}
      <PolicyNavigation currentPolicy="trust-center" onNavigate={onNavigate} />
    </div>
  );
};

export default TrustCenterContent;

